import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { BusinessUserProfileForm } from "@tootsweet/model/entities/BusinessUserProfileForm";
import { tt } from "../../../utils/i18n/i18n";
import { STORAGE_SIGNUP_FORM_VALUES_KEY } from "../../../utils/constants";
import "../styles.scss";
import { EMAIL_VERIFICATION } from "../../../utils/routes/routes";
import SignUpLayout from "./SignUpLayout";
import { useUrlQuery } from "../../../utils/hooks/useUrlQuery";
import KC_Heading1 from "../../../designsystem/typography/KC-Heading/KC_Heading1";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import {
  useCountryCodeSelector,
  useSubscriptionPlansSelector
} from "../../../redux/actions/app";
import OnboardingForm from "../../17_JetpackOnboarding/components/OnboardingForm";

const AMBASSADOR_LIGHT_PLAN_ID = "69b866a25715fc00f60f67af";
const LOYALTY_LIGHT_PLAN_ID = "69b9bb4a5715fc00f60f67b8";
const AMBASSADOR_PROGRAM_PLAN_ID = "69ba55cf5715fc00f60f67b9";
const LOYALTY_PROGRAM_PLAN_ID = "69ba579c5715fc00f60f67ba";
const PREMIUM_PLAN_ID = "69ba58025715fc00f60f67bb";

export default function SignUp() {
  const query = useUrlQuery();
  const history = useHistory<BusinessUserProfileForm>();
  const location = useLocation();
  const plans = useSubscriptionPlansSelector();
  const countryCode = useCountryCodeSelector();

  const handleSubmit = async (values: BusinessUserProfileForm) => {
    localStorage.setItem(
      STORAGE_SIGNUP_FORM_VALUES_KEY,
      JSON.stringify(values)
    );

    const search = new URLSearchParams(location.search);
    const planId = search.get("planId");

    if (!planId && plans?.length) {
      const defaultPlan = plans.find((p) => p.isDefault);
      if (defaultPlan) {
        search.set("planId", defaultPlan._id);
      }
    }

    history.push(
      EMAIL_VERIFICATION + "?" + search.toString(),
      values
    );
  };

  const initialValues: BusinessUserProfileForm = useMemo(() => {
    const stored = localStorage.getItem(STORAGE_SIGNUP_FORM_VALUES_KEY);
    const res = stored ? JSON.parse(stored) : {};

    if (!res.shippingAddress?.countryCode) {
      if (!res.shippingAddress) {
        res.shippingAddress = {};
      }

      res.shippingAddress.countryCode = countryCode;
    }

    return res;
  }, [plans, countryCode]);

  const isPromoCode = useMemo(() => {
    return !!query.get("pc");
  }, [query]);

  const isLogin = useMemo(() => {
    return !!query.get("login");
  }, [query]);

  const plan = useMemo(() => {
    if (!plans) {
      return null;
    }

    const planId = query.get("planId");

    if (!planId) {
      return plans.find((p) => p.isDefault) || null;
    }

    return plans?.find((p) => p._id === planId);
  }, [query, plans]);

  const isPaidOnboarding = useMemo(() => {
    return isPromoCode || !!plan;
  }, [isPromoCode, plan]);

  const TITLE = useMemo(() => {
    if (isLogin) {
      return (
        <KC_Heading1
          text={tt("signup_access_account")}
          className="text-center"
        />
      );
    }

    if (!isLogin && isPromoCode) {
      return (
        <KC_Heading1
          text={tt("signup_heading_promocode")}
          className="text-center"
        />
      );
    }

    let text = "";

    switch (plan?._id) {
      case AMBASSADOR_LIGHT_PLAN_ID:
        text = tt("signup_wording_ambassador_light");
        break;
      case AMBASSADOR_PROGRAM_PLAN_ID:
        text = tt("signup_wording_ambassador_premium");
        break;
      case LOYALTY_LIGHT_PLAN_ID:
        text = tt("signup_wording_loyalty_light");
        break;
      case LOYALTY_PROGRAM_PLAN_ID:
        text = tt("signup_wording_loyalty_premium");
        break;
      case PREMIUM_PLAN_ID:
        text = tt("signup_wording_super_premium");
        break;
      default:
        text = tt("signup_heading");
        break;
    }

    return <KC_Heading1 text={text} className="text-center" />;
  }, [plan, isLogin, isPromoCode]);

  const handleLoginClick = () => {
    history.push(EMAIL_VERIFICATION + "?login=1");
  };

  const submitButtonText = isPaidOnboarding
    ? tt("create_my_game")
    : tt("create_my_free_game");

  if (!plans?.length) {
    return null;
  }

  return (
    <SignUpLayout>
      <div className="d-flex flex-column align-items-center sign-up-text">
        {TITLE}
      </div>
      <div
        id="info-onboarding"
        className="d-flex flex-column justify-content-center"
      >
        <OnboardingForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          submitButtonText={submitButtonText}
        />
        <KC_UIText
          id="have-already-account"
          weight="reg"
          text={tt("signup_already_have_account")}
          className="underline cursor-pointer ml-auto mr-auto"
          onClick={handleLoginClick}
        />
      </div>
    </SignUpLayout>
  );
}
